/**
 * Force-action confirm dialog (Phase 2, PeriodModal's own row action) — lets an operator skip the
 * sweep's cadence and force an immediate license-remove or unlink for the customer's OPEN period. One
 * confirm step, then the SAME dialog shows what actually happened: a dry-run badge (the server may
 * answer with dry_run=true, e.g. while the Zoom app is being verified) and the raw `actions` counter map
 * as a compact list. 409 (an open intent already exists for this period) and 503 (Zoom app unconfigured)
 * are rendered VERBATIM, never swallowed into generic copy, exactly like every other server refusal in
 * this module. (rev 9, 21/7: the managed-domain 409 that used to fire on force-unlink is gone — see
 * PeriodModal.tsx's own banner.)
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "@/api/client";
import { Button, Modal, StatusPill } from "@/components/ui";
import { useForceLicenseRemove, useForceUnlink } from "@/features/pool/hooks";
import { outcomeText } from "@/features/pool/forceOutcome";
import { PoolWriteModeNotice, usePoolWriteIsLive } from "@/features/pool/PoolWriteModeNotice";
import type { PoolForceActionResult } from "@/api/types";
import type { TKey } from "@/i18n/keys";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

export type ForceActionKind = "license-remove" | "unlink";

const DIALOG_TITLE_KEY: Record<ForceActionKind, TKey> = {
  "license-remove": "pool.period.force.licenseRemoveDialogTitle",
  unlink: "pool.period.force.unlinkDialogTitle",
};

const DIALOG_BODY_KEY: Record<ForceActionKind, TKey> = {
  "license-remove": "pool.period.force.licenseRemoveDialogBody",
  unlink: "pool.period.force.unlinkDialogBody",
};

const CONFIRM_BUTTON_KEY: Record<ForceActionKind, TKey> = {
  "license-remove": "pool.period.force.licenseRemoveButton",
  unlink: "pool.period.force.unlinkButton",
};

export function ForceActionDialog({
  kind,
  periodId,
  email,
  onClose,
}: {
  kind: ForceActionKind;
  periodId: number;
  email: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const removeMut = useForceLicenseRemove(email);
  const unlinkMut = useForceUnlink(email);
  const mut = kind === "license-remove" ? removeMut : unlinkMut;
  const isLive = usePoolWriteIsLive();
  // R1 fail-safe (adversarial review): isLive === null/undefined = write mode UNKNOWN (system-state
  // query loading/errored). Unknown is NOT "off" — the confirm stays DISABLED and the body shows a
  // neutral "checking" line, never the definite "system is off / no real effect" claim.
  const writeModeUnknown = isLive !== true && isLive !== false;

  const [result, setResult] = useState<PoolForceActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    mut.mutate(periodId, {
      onSuccess: (res) => setResult(res),
      onError: (e) => setError(errMessage(e, t("state.errorTitle"))),
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t(DIALOG_TITLE_KEY[kind])}
      size="sm"
      footer={
        result ? (
          <Button variant="secondary" onClick={onClose}>
            {t("action.close")}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("action.cancel")}
            </Button>
            {/* R1 late ruling (mirrors UnlinkOnlyDialog / BulkForceDialog): danger tone ONLY once the
                write mode is CONFIRMED live. `isLive === false` (off) and `null` (loading/errored) both
                fall through to the neutral tone — an unconfirmed state is never dressed as danger.
                Fail-safe: while the write mode is UNKNOWN the confirm is also DISABLED so a real force
                can never fire before the state is known. */}
            <Button
              variant={isLive === true ? "danger" : "secondary"}
              disabled={writeModeUnknown}
              loading={mut.isPending}
              onClick={submit}
            >
              {t(CONFIRM_BUTTON_KEY[kind])}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        {!result && (
          <>
            <PoolWriteModeNotice />
            {/* F1 body honesty (R1 late ruling + fail-safe): three honest states. true → the "happens
                immediately" danger sentence. false → the shared "system is off" test-mode body.
                null/undefined (loading/errored) → a NEUTRAL "checking write mode" line, never the
                definite "off / no real effect" claim while we don't yet know the write mode. */}
            <p className="text-(length:--text-sm) text-(--text)">
              {isLive === true
                ? t(DIALOG_BODY_KEY[kind], { email })
                : isLive === false
                  ? t("pool.period.force.testModeBody")
                  : t("pool.period.force.checkingWriteMode")}
            </p>
          </>
        )}

        {/* The server's refusal, rendered verbatim (409 open-intent, 503 unconfigured) — never
            paraphrased, exactly like every other refusal elsewhere in this module. */}
        {error && (
          <p role="alert" className="text-(length:--text-sm) text-(--danger)">
            {error}
          </p>
        )}

        {result && (
          <div className="flex flex-col gap-2 rounded-(--radius) border border-(--border) bg-(--surface-sunken) p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-(length:--text-sm) font-semibold text-(--text-strong)">
                {t("pool.period.force.resultTitle")}
              </p>
              {/* "Ran for real" next to a guard-skip read as a contradiction (คุณใหม่, 22/7 —
                  first master force): the badge now reflects what HAPPENED, not the mode. Green
                  only when an ok_* write actually landed; a live-mode run where every action was
                  a skip/abort wears the neutral "nothing was sent" badge instead. */}
              <StatusPill
                tone={
                  result.dry_run
                    ? "warn"
                    : Object.keys(result.actions).some((k) => k.startsWith("ok_"))
                      ? "ok"
                      : "neutral"
                }
              >
                {result.dry_run
                  ? t("pool.period.force.dryRunBadge")
                  : Object.keys(result.actions).some((k) => k.startsWith("ok_"))
                    ? t("pool.period.force.liveBadge")
                    : t("pool.period.force.noWriteBadge")}
              </StatusPill>
            </div>
            {Object.keys(result.actions).length === 0 ? (
              <p className="text-(length:--text-xs) text-(--text-muted)">
                {t("pool.period.force.noActions")}
              </p>
            ) : (
              <ul className="flex flex-col gap-1 text-(length:--text-sm)">
                {Object.entries(result.actions)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([action, n]) => (
                    <li key={action} className="flex items-center justify-between gap-3">
                      <span className="text-(--text)">{outcomeText(action, t)}</span>
                      {n > 1 && (
                        <span className="tabular font-medium text-(--text-strong)">{n}</span>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
